/**
 * BehaviorGraph reference kind — inline `configuration.target` sockets of the
 * single-graph `graph` field. Extracted from referenceIdentity.ts when the
 * registry was generalized to multiple reference kinds; semantics unchanged.
 *
 * Location key = the node index as a string. Multi-graph `graphs[]` data is not
 * yet handled (spec item 3).
 */

import type { SceneObject } from '../../../core/SceneObject';
import type { BehaviorData } from '../../../scenedata/behaviordata';
import type { GraphData, GraphNode } from '../../../scenedata/behaviors/behaviorgraphdata';
import type { ReferenceKind, ReferenceLocation } from '../referenceIdentity';

interface BehaviorGraphLike {
    getData?: () => (BehaviorData & { graph?: GraphData }) | undefined;
    commit?: (patch: { graph: GraphData }) => Promise<unknown>;
}

function behaviorGraphOf(owner: SceneObject): BehaviorGraphLike | null {
    return (owner.getBehavior?.('BehaviorGraph') as BehaviorGraphLike | null | undefined) ?? null;
}

function graphOf(bg: BehaviorGraphLike | null): GraphData | undefined {
    try {
        return bg?.getData?.()?.graph;
    } catch {
        return undefined;
    }
}

/** The inline target path of a node, or null when the `target` socket is
 *  absent, dynamic (a node-ref input), or not a non-empty string. */
function inlineTargetPath(node: GraphNode | undefined): string | null {
    const cfg = node?.configuration?.target;
    if (!cfg || !Array.isArray(cfg.value)) return null;
    const p = cfg.value[0];
    return typeof p === 'string' && p.length > 0 ? p : null;
}

export const behaviorGraphReferenceKind: ReferenceKind = {
    behaviorType: 'BehaviorGraph',

    discover(owner: SceneObject): ReferenceLocation[] {
        const graph = graphOf(behaviorGraphOf(owner));
        if (!graph?.nodes) return [];
        const out: ReferenceLocation[] = [];
        graph.nodes.forEach((node, i) => {
            const path = inlineTargetPath(node);
            if (path != null) out.push({ key: String(i), path });
        });
        return out;
    },

    async write(owner: SceneObject, updates: Array<{ key: string; path: string }>): Promise<void> {
        const bg = behaviorGraphOf(owner);
        const graph = graphOf(bg);
        if (!graph?.nodes || !bg?.commit) return;
        const cloned = structuredClone(graph);
        for (const { key, path } of updates) {
            const cfg = cloned.nodes[Number(key)]?.configuration?.target;
            if (cfg && Array.isArray(cfg.value)) cfg.value = [path];
        }
        await bg.commit({ graph: cloned });
    },
};
